import { useMutationState } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DiscountOfferModal } from './DiscountOfferModal';
import { PricingStep } from './PricingStep';
import { fireSideCannons } from './fireSideCannons';
import {
  ONBOARDING_DISCOUNT_PERCENT_OFF,
  useIssueOnboardingDiscount,
  useOnboardingDiscount,
} from './onboardingDiscount';
import { useRegisterOnboardingFinish } from './onboardingExit';
import { Endpoint } from '@/Config/api/endpoint';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { GettingStartedDiscountTrigger, PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { SCENARIO_ID } from '@/context';

export const SKIP_CONFETTI_KEY_PREFIX = '__qc_onboarding_skip_confetti:';
export const EXIT_INTENT_OFFER_KEY_PREFIX = '__qc_onboarding_exit_intent_offer:';

const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

const hasFinePointer = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia(FINE_POINTER_QUERY).matches;

const wasExitIntentOffered = (): boolean => {
  try {
    return sessionStorage.getItem(`${EXIT_INTENT_OFFER_KEY_PREFIX}${SCENARIO_ID}`) !== null;
  } catch {
    return false;
  }
};

const markExitIntentOffered = (): void => {
  try {
    sessionStorage.setItem(`${EXIT_INTENT_OFFER_KEY_PREFIX}${SCENARIO_ID}`, '1');
  } catch {
    // Storage blocked: the offer may show again in this tab, which is harmless.
  }
};

interface PricingStepContainerProps {
  onBack: () => void;
  onSkipForNow: () => void | Promise<void>;
  isSkipPending?: boolean;
}

export const PricingStepContainer = ({
  onBack,
  onSkipForNow,
  isSkipPending = false,
}: PricingStepContainerProps) => {
  const { t } = useTranslation('getting_started');

  const { state: discount, isClaimable } = useOnboardingDiscount();
  const issueDiscount = useIssueOnboardingDiscount();
  const { capture } = usePostHogAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [offerTrigger, setOfferTrigger] = useState<GettingStartedDiscountTrigger>('skip');
  // A successful checkout mutation still counts: navigation to Stripe can lag the settled mutation by seconds.
  const isCheckoutStarting =
    useMutationState({
      filters: {
        mutationKey: [Endpoint.subscription.update],
        predicate: (mutation) =>
          mutation.state.status === 'pending' || mutation.state.status === 'success',
      },
      select: (mutation) => mutation.state.status,
    }).length > 0;

  const isFinishingRef = useRef(false);
  useEffect(() => {
    if (!isSkipPending) isFinishingRef.current = false;
  }, [isSkipPending]);

  const finishSetup = useCallback(() => {
    if (isFinishingRef.current) return false;
    isFinishingRef.current = true;
    // The offer modal already fired confetti when it opened.
    if (discount && isClaimable) {
      sessionStorage.setItem(`${SKIP_CONFETTI_KEY_PREFIX}${SCENARIO_ID}`, '1');
    }
    void onSkipForNow();
    return true;
  }, [discount, isClaimable, onSkipForNow]);

  const offerDiscount = useCallback(
    (trigger: GettingStartedDiscountTrigger, onUnavailable: () => void, onFailure: () => void) => {
      issueDiscount.mutate(undefined, {
        onSuccess: (data) => {
          // 204 (e.g. ineligible after upgrading in another tab) returns no code —
          // don't open an empty modal + fire confetti for nothing.
          if (!data?.code) {
            onUnavailable();
            return;
          }
          setOfferTrigger(trigger);
          setIsModalOpen(true);
          fireSideCannons();
          // Funnel denominator: the offer was actually shown to the user.
          capture(PostHogEventEnum.GettingStartedDiscountOffered, { trigger });
        },
        onError: onFailure,
      });
    },
    [issueDiscount, capture],
  );

  // Shared by Skip and X: a code already on screen means finishing, not offering again.
  const offerOrFinish = useCallback(
    (trigger: GettingStartedDiscountTrigger, finish: () => void) => {
      if (discount && isClaimable) {
        finish();
        return;
      }
      offerDiscount(trigger, finish, () => {
        // Issuance failed (Stripe outage, etc). Don't trap the user on Pricing
        // — surface the failure and let them advance.
        toast.error(t('discount_modal.skip_fallback_toast'));
        finish();
      });
    },
    [discount, isClaimable, offerDiscount, t],
  );

  const handleSkipClick = useCallback(
    () => offerOrFinish('skip', finishSetup),
    [offerOrFinish, finishSetup],
  );

  // The X records an exit only when setup really finishes, never for opening or claiming the offer.
  const finishFromExit = useCallback(() => {
    if (finishSetup()) capture(PostHogEventEnum.GettingStartedLeftViaFinish);
  }, [finishSetup, capture]);

  const handleExitClick = useCallback(() => {
    if (isFinishingRef.current || issueDiscount.isPending || isModalOpen) return false;
    offerOrFinish('x_button', finishFromExit);
    return false;
  }, [issueDiscount.isPending, isModalOpen, offerOrFinish, finishFromExit]);
  useRegisterOnboardingFinish(handleExitClick);

  const handleExitIntent = useCallback(() => {
    if (
      discount ||
      isModalOpen ||
      issueDiscount.isPending ||
      isCheckoutStarting ||
      isSkipPending ||
      isFinishingRef.current ||
      wasExitIntentOffered()
    ) {
      return;
    }
    markExitIntentOffered();
    offerDiscount(
      'exit_intent',
      () => {},
      () => {},
    );
  }, [
    discount,
    isModalOpen,
    issueDiscount.isPending,
    isCheckoutStarting,
    isSkipPending,
    offerDiscount,
  ]);
  const handleExitIntentRef = useRef(handleExitIntent);
  handleExitIntentRef.current = handleExitIntent;

  useEffect(() => {
    if (!hasFinePointer()) return;
    const handleMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget || event.clientY > 0) return;
      handleExitIntentRef.current();
    };
    document.addEventListener('mouseout', handleMouseOut);
    return () => document.removeEventListener('mouseout', handleMouseOut);
  }, []);

  const closeModal = useCallback(() => setIsModalOpen(false), []);
  // An exit-intent offer interrupts a user still on Pricing; closing it must not end setup.
  const finishAfterOffer = useCallback(() => {
    if (offerTrigger === 'exit_intent') return;
    if (offerTrigger === 'x_button') finishFromExit();
    else finishSetup();
  }, [offerTrigger, finishFromExit, finishSetup]);

  const handleClaimDiscount = useCallback(() => {
    capture(PostHogEventEnum.GettingStartedDiscountClaimed, { trigger: offerTrigger });
    closeModal();
  }, [capture, closeModal, offerTrigger]);

  const handleUseLater = useCallback(() => {
    capture(PostHogEventEnum.GettingStartedDiscountUseLater, { trigger: offerTrigger });
    closeModal();
    finishAfterOffer();
  }, [capture, closeModal, offerTrigger, finishAfterOffer]);

  const handleDismiss = useCallback(() => {
    closeModal();
    finishAfterOffer();
  }, [closeModal, finishAfterOffer]);

  const isDiscountActive = isClaimable;

  return (
    <div
      className="flex flex-col gap-6 bg-card border border-border rounded-xl p-6 min-h-[500px]"
      data-testid="gs-pricing-container"
    >
      <div>
        <p
          className="text-xl font-semibold text-foreground"
          data-testid="gs-pricing-container-title"
        >
          {t('steps.gs_step_5.title')}
        </p>
        <p
          className="text-sm font-medium text-muted-foreground mt-1"
          data-testid="gs-pricing-container-subtitle"
        >
          <Trans
            i18nKey="steps.gs_step_5.description"
            t={t}
            components={{ bold: <strong className="font-semibold text-foreground" /> }}
          />
        </p>
      </div>

      <div className="flex flex-1 flex-col">
        <PricingStep
          discountApplied={isDiscountActive}
          discountPercentOff={ONBOARDING_DISCOUNT_PERCENT_OFF}
          promoCode={isDiscountActive ? discount?.code : undefined}
        />
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button
          size="sm"
          variant="link"
          onClick={onBack}
          className="!text-foreground/60 hover:!opacity-100 hover:!text-foreground"
          data-testid="gs-pricing-container-back-button"
        >
          <ChevronLeft />
          {t('back')}
        </Button>
        <Button
          size="sm"
          variant="link"
          onClick={handleSkipClick}
          disabled={isSkipPending || issueDiscount.isPending}
          className="!text-foreground/60 hover:!opacity-100 hover:!text-foreground"
          data-testid="gs-pricing-container-skip-button"
        >
          {isSkipPending || issueDiscount.isPending ? (
            <Spinner className="size-4" />
          ) : (
            <>
              {t('pricing_step.skip_for_now')}
              <ChevronRight />
            </>
          )}
        </Button>
      </div>

      {discount && (
        <DiscountOfferModal
          open={isModalOpen}
          code={discount.code}
          expiresAt={new Date(discount.expires_at).getTime()}
          onClose={handleDismiss}
          onRedeem={handleClaimDiscount}
          onUseLater={handleUseLater}
        />
      )}
    </div>
  );
};
